import React from "react";

/**
 * Horizontal price range bar — shows where `current` sits between `low` and `high`.
 *
 * Props:
 *   label: string           — e.g. "Today's range" / "52 week range"
 *   low: number             — lower bound
 *   high: number            — upper bound
 *   current: number         — current price
 *   testId?: string         — data-testid on the container
 */
export default function PriceRangeBar({ label, low, high, current, testId }) {
  if (low == null || high == null || current == null || high <= low) {
    return null;
  }
  const pct = Math.max(0, Math.min(100, ((current - low) / (high - low)) * 100));

  const fmt = (v) => {
    if (v >= 1000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    return `$${v.toFixed(2)}`;
  };

  return (
    <div className="space-y-1" data-testid={testId || "price-range-bar"}>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-gray-400 uppercase tracking-wide font-semibold">{label}</span>
        <span className="font-mono text-cyan-300 text-[11px]">{fmt(current)}</span>
      </div>

      {/* Bar track */}
      <div className="relative h-1.5 rounded-full bg-gray-700">
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-sm bg-cyan-400 border border-cyan-300 shadow-[0_0_6px_rgba(34,211,238,0.7)]"
          style={{ left: `calc(${pct}% - 6px)` }}
          title={`${pct.toFixed(1)}% between ${fmt(low)} and ${fmt(high)}`}
        />
      </div>

      <div className="flex items-center justify-between text-[10px] font-mono text-gray-400">
        <span>{fmt(low)}</span>
        <span>{fmt(high)}</span>
      </div>
    </div>
  );
}
